import { useEffect, useState, useMemo } from 'react';
import { useForm, FieldData, Select, TextField, SliderPopover } from 'vopay';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { CircleButton } from '#shared/components/UI';
import useLabels from '#shared/hooks/useApi/useLabels';

export type FormValues = {
  clientType: 'individual' | 'business' | '';
  accountStatus: 'active' | 'inactive' | '';
  keyword: string;
  label: string;
  balance: [number, number] | null;
};

type UseFilterFormConfigProps = {
  query: Partial<
    Omit<FormValues, 'balance'> & {
      balance: string;
    }
  >;
  onSubmit: (values: FormValues) => void;
  /** Balance slider bounds, fetched and owned by the ClientAccountList root (which raises the loader). */
  balanceRange: [number, number];
};

const initialValues: FormValues = {
  clientType: '',
  accountStatus: '',
  keyword: '',
  label: '',
  balance: null,
};

export function useFilterFormConfig({ query, onSubmit, balanceRange }: UseFilterFormConfigProps) {
  const { labels, fetchLabels } = useLabels();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const alertDialog = useAlertDialog();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredLabels = useMemo(
    () =>
      labels
        .filter((label) => label.toLowerCase().includes(searchTerm.toLowerCase()))
        .slice(0, 100)
        .map((label) => ({ label, value: label })),
    [labels, searchTerm],
  );

  useEffect(() => {
    ctx.setValues(
      Object.entries(query).reduce((acc, [field, val]) => {
        return {
          ...acc,
          [field]: field === 'balance' ? val.split('|').map(Number) : val,
        };
      }, ctx.values),
    );
  }, [query]);

  useEffect(() => {
    fetchLabels().catch((error) => {
      // The hook already logged; surface the failure the way the client list always has.
      alertDialog.error(
        t('labelsErrorTitle', { defaultValue: 'Failed to fetch client account labels' }),
        error instanceof Error ? error.message : String(error),
        {
          abortLabel: tGlobal('close', { defaultValue: 'Close' }),
        },
      );
    });
  }, []);

  // The root fetches the balance range (and raises the blocking loader); seed the slider default from it
  // once it arrives, unless the URL query already carries a balance filter.
  useEffect(() => {
    if (query.balance) return;

    if (balanceRange[0] === 0 && balanceRange[1] === 0) return;

    ctx.setFieldValue('balance', balanceRange);
  }, [balanceRange]);

  const config: FieldData<FormValues>[] = [
    {
      fieldName: 'clientType',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <Select
            version="2"
            {...getFormikData<FormValues['clientType']>({})}
            sort={false}
            placeholder={t('type', { defaultValue: 'Type' })}
            showClear
            options={[
              { label: t('ClientAccountType.individual', { defaultValue: 'Individual' }), value: 'individual' },
              { label: t('ClientAccountType.business', { defaultValue: 'Business' }), value: 'business' },
            ]}
          />
        );
      },
    },
    {
      fieldName: 'accountStatus',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <Select
            version="2"
            {...getFormikData<FormValues['accountStatus']>({})}
            sort={false}
            placeholder={t('status', { defaultValue: 'Status' })}
            showClear
            options={[
              { label: t('Status.active', { defaultValue: 'Active' }), value: 'active' },
              { label: t('Status.inactive', { defaultValue: 'Inactive' }), value: 'inactive' },
            ]}
          />
        );
      },
    },
    {
      fieldName: 'label',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <Select
            version="2"
            {...getFormikData<FormValues['label']>({})}
            sort={false}
            showClear
            placeholder={t('Select.label', { defaultValue: 'Label' })}
            filterable
            options={filteredLabels}
            onFilter={(e) => {
              e.preventDefault();

              e.debounce(() => {
                setSearchTerm(e.filter);
              }, 300);
            }}
          />
        );
      },
    },
    {
      fieldName: 'balance',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField(values) {
        // don't include balance in the filter request; otherwise the response will ONLY
        // return accounts with a balance
        if (values.balance === null) {
          return undefined;
        }

        if (balanceRange[0] === values.balance[0] && balanceRange[1] === values.balance[1]) {
          return undefined;
        }

        return values.balance?.join('|');
      },
      renderField: ({ ctx }) => {
        if (!ctx.values.balance) {
          return null;
        }

        return (
          <SliderPopover
            min={balanceRange[0]}
            max={balanceRange[1]}
            value={ctx.values.balance}
            onApply={(range) => {
              ctx.setFieldValue('balance', range);
            }}
          />
        );
      },
    },
    {
      fieldName: 'keyword',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData, ctx }) => {
        return (
          <TextField
            variant="outlineShadow"
            {...getFormikData<FormValues['keyword']>({})}
            placeholder={t('TextField.keyword', { defaultValue: 'Keyword' })}
            elementRight={
              ctx.values.keyword ? (
                <CircleButton
                  type="button"
                  icon="close"
                  circleFill={false}
                  onClick={() => ctx.setFieldValue('keyword', '')}
                  style={{
                    marginRight: '8px',
                  }}
                />
              ) : (
                false
              )
            }
          />
        );
      },
    },
  ];

  const { renderFormElements, ctx } = useForm<FormValues>({
    initialValues,
    config,
    onSubmit,
  });

  return {
    ctx,
    renderFormElements: () => renderFormElements({ config }),
  };
}
